import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  GROUP_ICONS,
  GROUP_MAX_MEMBERS,
  GROUP_NAME_MAX,
  STICKERS,
  socialLine,
  type GroupIcon,
  type ChatMessage,
  type ChatOpen,
  type ChatSender,
  type Conversation,
  type PersonAction,
  type PersonView,
  type StickerId,
  type TeamReply,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { PersonCard, PersonPicture } from '../people/person-card';
import { MessageList } from './message-list';
import { MoneySheet } from './money';
import { useDismiss } from '../lib/use-dismiss';
import { StreakBadge, StreakButton } from '../streaks/streak';
import { TeamChat, TeamRow } from './team-chat';
import { useConversation } from './use-conversation';
import type { Realtime } from './use-realtime';
import { useBackLayer } from '../lib/back-stack';
import { notify } from '../lib/alerts';
import { actionKey } from '../lib/format';

const whenOf = (iso: string) => {
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  return today
    ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const previewOf = (message: ChatMessage | null) => {
  if (!message) return 'Say hello';
  const body =
    message.deletedAt || message.text === null
      ? 'Message deleted'
      : message.kind === 'sticker'
        ? `Sticker: ${STICKERS[message.text as StickerId] ?? ''}`
        : message.text;
  if (message.kind === 'voice' && !message.deletedAt)
    return `${message.mine ? 'You' : message.sender.firstName}: 🎤 Voice note`;
  if (message.kind === 'system') return body;
  if (message.kind === 'social') return socialLine(message.text ?? '', message.mine ? 'You' : message.sender.firstName);
  return `${message.mine ? 'You' : message.sender.firstName}: ${body}`;
};

// a picture with a green dot when they are in the game now
function Avatar({ person, online = false }: { person: ChatSender; online?: boolean }) {
  return (
    <span className="chat-avatar">
      <PersonPicture person={person} />
      {online && <i className="chat-online" aria-label="Online now" />}
    </span>
  );
}

// a group's picture, chosen from a small set
function IconPicker({ value, onChange }: { value: GroupIcon; onChange: (icon: GroupIcon) => void }) {
  return (
    <div className="chat-icons" role="radiogroup" aria-label="Group picture">
      {GROUP_ICONS.map((icon) => (
        <button
          key={icon}
          type="button"
          role="radio"
          aria-checked={value === icon}
          aria-label={`Picture ${icon}`}
          className={value === icon ? 'active' : undefined}
          onClick={() => onChange(icon)}
        >
          {icon}
        </button>
      ))}
    </div>
  );
}

// choose people for a group: those you know (People) and those you chat with, as chips, with a search box
function PeoplePicker({
  exclude,
  chosen,
  onChosen,
}: {
  exclude: string[];
  chosen: string[];
  onChosen: (lifeIds: string[]) => void;
}) {
  const [people, setPeople] = useState<ChatSender[] | null>(null);
  const [find, setFind] = useState('');
  useEffect(() => {
    void Promise.all([api.people.get(), api.chat.list()])
      .then(([known, chats]) => {
        const byLife = new Map<string, ChatSender>();
        for (const person of known.people) if (!person.blocked) byLife.set(person.lifeId, { ...person, userId: '' });
        for (const chat of chats.chats) if (chat.other) byLife.set(chat.other.lifeId, chat.other);
        setPeople([...byLife.values()].filter((person) => !exclude.includes(person.lifeId)));
      })
      .catch(() => setPeople([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!people) return <p className="chat-empty">Finding your people…</p>;
  if (!people.length)
    return <p className="chat-empty">No one to add yet. Say hello to people in the city or chat with them first.</p>;
  const needle = find.trim().replace(/^@/, '').toLowerCase();
  const shown = needle
    ? people.filter(
        (person) => person.firstName.toLowerCase().includes(needle) || person.username?.toLowerCase().includes(needle),
      )
    : people;
  const toggle = (lifeId: string) =>
    onChosen(chosen.includes(lifeId) ? chosen.filter((id) => id !== lifeId) : [...chosen, lifeId]);
  return (
    <div className="chat-pick-wrap">
      {people.length > 6 && (
        <input
          className="chat-find"
          aria-label="Find someone you know"
          placeholder="Find by name or @username"
          value={find}
          onChange={(event) => setFind(event.target.value)}
        />
      )}
      <p className="chat-hint">People you know</p>
      <ul className="chat-pick">
        {shown.map((person) => {
          const on = chosen.includes(person.lifeId);
          return (
            <li key={person.lifeId}>
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={`${person.firstName}${person.username ? ` @${person.username}` : ''}`}
                className={on ? 'chat-chip active' : 'chat-chip'}
                onClick={() => toggle(person.lifeId)}
              >
                <PersonPicture person={person} />
                <span>{person.username ? `@${person.username}` : person.firstName}</span>
                <b aria-hidden="true">{on ? '✓' : '+'}</b>
              </button>
            </li>
          );
        })}
        {!shown.length && <li className="chat-empty">Nobody you know by that name.</li>}
      </ul>
    </div>
  );
}

// starting a group: a name and the people in it
function NewGroup({ onDone, onCancel }: { onDone: (conversation: Conversation) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<GroupIcon>('👥');
  const [chosen, setChosen] = useState<string[]>([]);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    setFailure(null);
    try {
      onDone((await api.chat.createGroup({ name: name.trim(), icon, lifeIds: chosen })).conversation);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="chat-panel" aria-label="New group">
      <div className="chat-group-name">
        <span className="chat-group-icon big">{icon}</span>
        <label className="chat-field">
          Group name
          <input
            value={name}
            maxLength={GROUP_NAME_MAX}
            placeholder="e.g. Five-a-side"
            onChange={(event) => setName(event.target.value)}
          />
        </label>
      </div>
      <IconPicker value={icon} onChange={setIcon} />
      <p className="chat-hint">
        Add people ({chosen.length}/{GROUP_MAX_MEMBERS - 1})
      </p>
      <PeoplePicker exclude={[]} chosen={chosen} onChosen={setChosen} />
      {failure && <ErrorNotice error={failure} />}
      <div className="chat-panel-actions">
        <button type="button" className="house-button quiet" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="house-button save"
          disabled={busy || !name.trim() || !chosen.length}
          onClick={() => void create()}
        >
          Start group
        </button>
      </div>
    </div>
  );
}

// a group's people and what its admins can do
function GroupInfo({
  conversation,
  myUserId,
  onChanged,
  onLeft,
}: {
  conversation: Conversation;
  myUserId: string | null;
  onChanged: (message: string) => void;
  onLeft: () => void;
}) {
  const group = conversation.group!;
  const admin = group.myRole === 'admin';
  const [adding, setAdding] = useState<string[] | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [icon, setIcon] = useState<GroupIcon>((group.icon as GroupIcon) ?? '👥');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [busy, setBusy] = useState(false);
  const act = async (body: Parameters<typeof api.chat.group>[1]) => {
    setBusy(true);
    setFailure(null);
    try {
      const reply = await api.chat.group(conversation.id, body);
      if (body.action === 'leave') onLeft();
      else onChanged(reply.message);
      return true;
    } catch (error) {
      setFailure(describeError(error));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="chat-panel" aria-label="Group info">
      {renaming !== null ? (
        <>
          <IconPicker value={icon} onChange={setIcon} />
          <div className="chat-field-row">
            <input
              aria-label="Group name"
              value={renaming}
              maxLength={GROUP_NAME_MAX}
              onChange={(event) => setRenaming(event.target.value)}
            />
            <button
              type="button"
              className="house-button save"
              disabled={busy || !renaming.trim()}
              onClick={() =>
                void act({ action: 'rename', name: renaming.trim(), icon }).then((ok) => ok && setRenaming(null))
              }
            >
              Save
            </button>
          </div>
        </>
      ) : (
        admin && (
          <button type="button" className="house-button quiet" onClick={() => setRenaming(group.name)}>
            ✎ Name and picture
          </button>
        )
      )}
      <p className="chat-hint">
        {group.members.length} {group.members.length === 1 ? 'person' : 'people'}
      </p>
      <ul className="chat-members">
        {group.members.map((member) => (
          <li key={member.userId}>
            <PersonPicture person={member} />
            <span>
              {member.firstName}
              {member.userId === myUserId && <small> (you)</small>}
              {member.role === 'admin' && <em className="chat-admin">admin</em>}
            </span>
            {admin && member.userId !== myUserId && (
              <span className="chat-member-actions">
                {member.role !== 'admin' && (
                  <button
                    type="button"
                    className="house-button quiet"
                    disabled={busy}
                    onClick={() => void act({ action: 'make_admin', userId: member.userId })}
                  >
                    Make admin
                  </button>
                )}
                <button
                  type="button"
                  className="house-button quiet"
                  disabled={busy}
                  onClick={() => void act({ action: 'remove', userId: member.userId })}
                >
                  Remove
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {admin && adding === null && group.members.length < GROUP_MAX_MEMBERS && (
        <button type="button" className="house-button" onClick={() => setAdding([])}>
          + Add people
        </button>
      )}
      {adding !== null && (
        <>
          <PeoplePicker exclude={group.members.map((member) => member.lifeId)} chosen={adding} onChosen={setAdding} />
          <div className="chat-panel-actions">
            <button type="button" className="house-button quiet" onClick={() => setAdding(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="house-button save"
              disabled={busy || !adding.length}
              onClick={() => void act({ action: 'add', lifeIds: adding }).then((ok) => ok && setAdding(null))}
            >
              Add
            </button>
          </div>
        </>
      )}
      {failure && <ErrorNotice error={failure} />}
      {confirmLeave ? (
        <span className="house-confirm">
          <small>Leave {group.name}?</small>
          <button
            type="button"
            className="house-button save"
            disabled={busy}
            onClick={() => void act({ action: 'leave' })}
          >
            Yes, leave
          </button>
          <button type="button" className="house-button quiet" onClick={() => setConfirmLeave(false)}>
            No
          </button>
        </span>
      ) : (
        <button type="button" className="house-button quiet chat-leave" onClick={() => setConfirmLeave(true)}>
          Leave group
        </button>
      )}
    </div>
  );
}

// The phone's chats: Chats and Groups tabs (unread counts, mark all as read, new group), and one open chat
// with its messages. A direct chat's header opens the person's card (ask to be partners, block and so on).
export function ChatScreen({
  realtime,
  open,
  onOpen,
  onClose,
  onUnread,
  myUserId,
  onWallet,
  onGoOver,
  onOpenShop,
}: {
  realtime: Realtime;
  // the chat to show, or null for the list
  open: ChatOpen | null;
  onOpen: (target: ChatOpen | null) => void;
  onClose: () => void;
  onUnread?: (count: number) => void;
  myUserId?: string | null;
  // money moved from a chat: the wallet's new balance
  onWallet?: (walletPence: number) => void;
  // an invite's "Go over": the way to their home
  onGoOver?: (hostLifeId: string) => void;
  // a shop card's "Open shop"
  onOpenShop?: (businessId: string) => void;
}) {
  const [tab, setTab] = useState<'chats' | 'groups'>(open?.kind === 'group' ? 'groups' : 'chats');
  const [chats, setChats] = useState<Conversation[] | null>(null);
  const [groups, setGroups] = useState<Conversation[] | null>(null);
  const [requests, setRequests] = useState<Conversation[]>([]);
  const [showRequests, setShowRequests] = useState(false);
  const [rowMenu, setRowMenu] = useState<string | null>(null);
  const [someone, setSomeone] = useState('');
  const [someoneFailure, setSomeoneFailure] = useState<FriendlyError | null>(null);
  const hold = useRef(0);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [menu, setMenu] = useState(false);
  // the chat's ⋯ menu and a held chat's menu close on a tap outside them or Escape
  const headerMenu = useRef<HTMLDivElement>(null);
  const rowMenuBox = useRef<HTMLDivElement>(null);
  useDismiss(menu, () => setMenu(false), headerMenu);
  useDismiss(Boolean(rowMenu), () => setRowMenu(null), rowMenuBox);
  const [panel, setPanel] = useState<'new-group' | 'info' | 'card' | 'team' | null>(null);
  // back from a panel (the team chat, a new group, someone's card) closes just the panel
  useBackLayer(panel !== null, () => setPanel(null));
  const [team, setTeam] = useState<TeamReply | null>(null);
  const [card, setCard] = useState<PersonView | null>(null);
  const [cardBusy, setCardBusy] = useState(false);
  const load = useCallback(async () => {
    setFailure(null);
    try {
      const [reply, teamReply] = await Promise.all([api.chat.list(), api.chat.team().catch(() => null)]);
      setChats(reply.chats);
      setGroups(reply.groups);
      setRequests(reply.requests);
      setTeam(teamReply);
      onUnread?.(
        [...reply.chats, ...reply.groups].reduce((sum, chat) => sum + chat.unread, 0) + (teamReply?.unread ?? 0),
      );
    } catch (error) {
      setFailure(describeError(error));
    }
  }, [onUnread]);
  useEffect(() => {
    if (!open) void load();
  }, [open, load, realtime.generation]);
  // a new message or a group change anywhere: the list changes
  useEffect(
    () =>
      realtime.listen((event) => {
        if ((event.type === 'message' || event.type === 'group' || event.type === 'team') && !open) void load();
      }),
    [realtime, open, load],
  );
  const current = useConversation(open, realtime, { myUserId });
  // money in a direct chat: the send / ask sheet, and a request being answered from its card
  const [moneyOpen, setMoneyOpen] = useState(false);
  const [moneyBusy, setMoneyBusy] = useState(false);
  const answerMoney = async (requestId: string, answer: 'pay' | 'decline' | 'cancel') => {
    setMoneyBusy(true);
    try {
      const reply = await api.money.answer(requestId, answer, `money-${answer}-${requestId}`);
      current.put(reply.message);
      onWallet?.(reply.walletPence);
      if (answer === 'pay') notify('💸 Paid');
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setMoneyBusy(false);
    }
  };
  // "Invite over": a card in the chat, and they can walk straight in for a while
  const [inviting, setInviting] = useState(false);
  const inviteOver = async (lifeId: string, name: string) => {
    setInviting(true);
    try {
      const reply = await api.visits.invite(lifeId, actionKey('invite'));
      current.put(reply.message);
      notify(`Invite sent 🏠 ${name} can walk straight in. Be home when they come!`);
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setInviting(false);
    }
  };
  // "🛍️ My shop": your shops with a shelf (looked up when a chat opens), shared as a card
  const [myShops, setMyShops] = useState<{ id: string; name: string }[]>([]);
  const [sharing, setSharing] = useState(false);
  const chatOpen = Boolean(open);
  useEffect(() => {
    if (!chatOpen) return;
    let live = true;
    api.business
      .list()
      .then((reply) => {
        if (live)
          setMyShops(reply.businesses.filter((item) => item.shelf).map((item) => ({ id: item.id, name: item.name })));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [chatOpen]);
  const shareShop = async (conversationId: string, businessId: string) => {
    setSharing(true);
    try {
      const reply = await api.business.share(conversationId, businessId, actionKey('shop-card'));
      current.put(reply.message);
    } catch (error) {
      notify(describeError(error).message);
    } finally {
      setSharing(false);
    }
  };
  useEffect(() => {
    setPanel(null);
    setMenu(false);
    if (open?.kind === 'group') setTab('groups');
  }, [open]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (panel) setPanel(null);
      else if (open) onOpen(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpen, onClose, panel]);
  const conversation = current.conversation;
  const other = conversation?.other;
  // the person's card, from People (closeness, ties); someone not met yet is a stranger
  const openCard = async () => {
    if (!other) return;
    const people = await api.people.get().catch(() => null);
    const known = [...(people?.people ?? []), ...(people?.here ?? [])].find((person) => person.lifeId === other.lifeId);
    setCard(
      known ?? {
        lifeId: other.lifeId,
        firstName: other.firstName,
        username: other.username,
        look: other.look,
        closeness: 0,
        tie: 'stranger',
        kin: null,
        pending: null,
        lastAt: null,
        here: false,
        blocked: false,
      },
    );
    setPanel('card');
  };
  const actOnCard = async (lifeId: string, body: PersonAction) => {
    setCardBusy(true);
    try {
      const reply = await api.people.act(lifeId, body, body.action === 'social' ? actionKey('social') : undefined);
      setCard(reply.person);
      if (body.action === 'block') onOpen(null);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setCardBusy(false);
    }
  };
  // "Message someone: @username"
  const messageSomeone = async (event: FormEvent) => {
    event.preventDefault();
    const username = someone.trim().replace(/^@/, '');
    if (username.length < 3) return;
    setSomeoneFailure(null);
    try {
      const reply = await api.chat.open({ kind: 'username', username });
      setSomeone('');
      if (reply.conversation.other) onOpen({ kind: 'direct', lifeId: reply.conversation.other.lifeId });
    } catch (error) {
      setSomeoneFailure(describeError(error));
    }
  };
  // hold a chat (or right-click it) to clear or delete it
  const removeChat = async (id: string, hide: boolean) => {
    setRowMenu(null);
    try {
      await api.chat.clear(id, hide);
      await load();
    } catch (error) {
      setFailure(describeError(error));
    }
  };
  const holdHandlers = (id: string) => ({
    onContextMenu: (event: React.MouseEvent) => {
      event.preventDefault();
      setRowMenu(id);
    },
    onPointerDown: (event: React.PointerEvent) => {
      if (event.pointerType === 'mouse') return;
      window.clearTimeout(hold.current);
      hold.current = window.setTimeout(() => setRowMenu(id), 550);
    },
    onPointerUp: () => window.clearTimeout(hold.current),
    onPointerLeave: () => window.clearTimeout(hold.current),
    onPointerCancel: () => window.clearTimeout(hold.current),
  });
  const openRow = (chat: Conversation) => {
    if (rowMenu) return;
    if (chat.kind === 'group') onOpen({ kind: 'group', conversationId: chat.id });
    else if (chat.other) onOpen({ kind: 'direct', lifeId: chat.other.lifeId });
  };
  const row = (chat: Conversation) => (
    <li key={chat.id}>
      <button type="button" className="chat-row" onClick={() => openRow(chat)} {...holdHandlers(chat.id)}>
        {chat.kind === 'group' ? (
          <span className="chat-group-icon big">{chat.group?.icon ?? '👥'}</span>
        ) : (
          chat.other && <Avatar person={chat.other} online={chat.online} />
        )}
        <span className="chat-row-text">
          <b>
            {chat.kind === 'group'
              ? chat.group?.name
              : chat.other?.username
                ? `@${chat.other.username}`
                : (chat.other?.firstName ?? 'Someone')}
            <StreakBadge streak={chat.streak} />
            {chat.lastMessage && <time>{whenOf(chat.lastMessage.createdAt)}</time>}
          </b>
          <small>{previewOf(chat.lastMessage)}</small>
        </span>
        {chat.unread > 0 && !chat.request && <em className="chat-unread">{chat.unread}</em>}
      </button>
      {rowMenu === chat.id && (
        <div className="chat-menu chat-row-menu" role="group" aria-label="Chat options" ref={rowMenuBox}>
          <button type="button" onClick={() => void removeChat(chat.id, false)}>
            Clear chat
          </button>
          {chat.kind === 'direct' && (
            <button type="button" onClick={() => void removeChat(chat.id, true)}>
              Delete chat
            </button>
          )}
          <button type="button" onClick={() => setRowMenu(null)}>
            Cancel
          </button>
        </div>
      )}
    </li>
  );
  const list = tab === 'chats' ? chats : groups;
  const unreadOf = (items: Conversation[] | null) => (items ?? []).reduce((sum, chat) => sum + chat.unread, 0);
  return (
    <div className="profile-screen chat-screen" role="dialog" aria-modal="true" aria-labelledby="chat-title">
      <div className="profile-frame chat-frame">
        <header className="profile-header chat-header">
          <button
            type="button"
            className="profile-back"
            onClick={() => (open ? onOpen(null) : panel === 'team' ? setPanel(null) : onClose())}
          >
            {open ? (open.kind === 'group' ? '‹ Groups' : '‹ Chats') : panel === 'team' ? '‹ Chats' : '‹ Phone'}
          </button>
          <h2 id="chat-title">
            {open && other ? (
              <button
                type="button"
                className="chat-title-button"
                onClick={() => void openCard()}
                aria-label={`${other.firstName}: open their card`}
              >
                <Avatar person={other} online={conversation?.online} /> {other.firstName}
                {other.username && <small>@{other.username}</small>}
              </button>
            ) : open && conversation?.group ? (
              <button
                type="button"
                className="chat-title-button"
                onClick={() => setPanel(panel === 'info' ? null : 'info')}
              >
                <span className="chat-group-icon">{conversation.group.icon}</span> {conversation.group.name}
                <small>{conversation.group.members.length} people</small>
              </button>
            ) : open ? (
              'Chat'
            ) : panel === 'team' ? (
              'London Life team'
            ) : (
              'Chats'
            )}
          </h2>
          {open && conversation?.kind === 'direct' && other && conversation.streak && (
            <StreakButton streak={conversation.streak} name={other.username ? `@${other.username}` : other.firstName} />
          )}
          {open && conversation && (
            <div className="chat-header-menu" ref={headerMenu}>
              <button
                type="button"
                className="house-button quiet"
                aria-expanded={menu}
                aria-label="Chat options"
                onClick={() => setMenu(!menu)}
              >
                ⋯
              </button>
              {menu && (
                <div className="chat-menu" role="group" aria-label="Chat options">
                  {other && (
                    <button type="button" onClick={() => (setMenu(false), void openCard())}>
                      View {other.firstName}'s card
                    </button>
                  )}
                  {conversation.group && (
                    <button type="button" onClick={() => (setMenu(false), setPanel('info'))}>
                      Group info
                    </button>
                  )}
                  {other && conversation.voice && (
                    <button
                      type="button"
                      onClick={() => (setMenu(false), void current.allowVoice(!conversation.voice!.iAllow))}
                    >
                      {conversation.voice.iAllow
                        ? `Stop voice notes from ${other.firstName}`
                        : `Allow voice notes from ${other.firstName}`}
                    </button>
                  )}
                  <button type="button" onClick={() => (setMenu(false), void current.clear(false))}>
                    Clear chat
                  </button>
                  {conversation.kind === 'direct' && (
                    <button
                      type="button"
                      onClick={() => (setMenu(false), void current.clear(true).then((ok) => ok && onOpen(null)))}
                    >
                      Delete chat
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </header>
        {!open && panel === 'team' && <TeamChat onRead={() => void load()} />}
        {!open && panel !== 'team' && (
          <div className="chat-list-wrap">
            <div className="chat-tabs" role="tablist" aria-label="Chats and groups">
              {(
                [
                  ['chats', 'Chats', unreadOf(chats) + (team?.unread ?? 0)],
                  ['groups', 'Groups', unreadOf(groups)],
                ] as const
              ).map(([key, label, unread]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={tab === key ? 'active' : undefined}
                  onClick={() => (setTab(key), setPanel(null))}
                >
                  {label}
                  {unread > 0 && <em className="chat-unread">{unread}</em>}
                </button>
              ))}
              <span className="chat-tabs-actions">
                {tab === 'groups' && panel !== 'new-group' && (
                  <button type="button" className="house-button" onClick={() => setPanel('new-group')}>
                    + New group
                  </button>
                )}
                {[...(chats ?? []), ...(groups ?? [])].some((chat) => chat.unread > 0) && (
                  <button
                    type="button"
                    className="house-button quiet"
                    onClick={() => void api.chat.readAll().then(load)}
                  >
                    Mark all as read
                  </button>
                )}
              </span>
            </div>
            {failure && <ErrorNotice error={failure} onRetry={() => void load()} />}
            {panel === 'new-group' && (
              <NewGroup
                onCancel={() => setPanel(null)}
                onDone={(created) => onOpen({ kind: 'group', conversationId: created.id })}
              />
            )}
            {tab === 'chats' && <TeamRow team={team} onOpen={() => setPanel('team')} />}
            {tab === 'chats' && (
              <form className="chat-someone" onSubmit={(event) => void messageSomeone(event)}>
                <input
                  aria-label="Message someone by username"
                  placeholder="✎ Message someone: @username"
                  value={someone}
                  maxLength={21}
                  autoCapitalize="off"
                  autoComplete="off"
                  onChange={(event) => (setSomeone(event.target.value), setSomeoneFailure(null))}
                />
                {someone.trim().replace(/^@/, '').length >= 3 && (
                  <button type="submit" className="house-button save">
                    Chat
                  </button>
                )}
              </form>
            )}
            {tab === 'chats' && someoneFailure && <ErrorNotice error={someoneFailure} />}
            {tab === 'chats' && requests.length > 0 && (
              <div className="chat-requests">
                <button
                  type="button"
                  className="chat-requests-head"
                  aria-expanded={showRequests}
                  onClick={() => setShowRequests(!showRequests)}
                >
                  <span aria-hidden="true">📨</span>
                  <span>
                    <b>Message requests</b>
                    <small>
                      {requests.length} {requests.length === 1 ? 'person' : 'people'} you haven’t chatted with
                    </small>
                  </span>
                  <em>{showRequests ? 'Hide' : 'See'}</em>
                </button>
                {showRequests && (
                  <>
                    <p className="chat-hint">No badge for these. Reply to move one to your chats, or delete it.</p>
                    <ul className="chat-list">{requests.map(row)}</ul>
                  </>
                )}
              </div>
            )}
            {list && !list.length && panel !== 'new-group' && (
              <p className="house-hint">
                {tab === 'chats'
                  ? 'No chats yet. Find someone under Who’s here at a place, in People, or by their @username.'
                  : 'No groups yet. Start one with + New group.'}
              </p>
            )}
            <ul className="chat-list">{list?.map(row)}</ul>
            {!!list?.length && <p className="chat-hint chat-hold-hint">Hold a chat to clear or delete it</p>}
          </div>
        )}
        {open && panel === 'info' && conversation?.group && (
          <GroupInfo
            conversation={conversation}
            myUserId={myUserId ?? null}
            onChanged={() => void current.reload()}
            onLeft={() => onOpen(null)}
          />
        )}
        {open && panel === 'card' && card && (
          <div className="chat-panel" aria-label={`${card.firstName}'s card`}>
            <ul className="people-list">
              <PersonCard person={card} busy={cardBusy} onAct={actOnCard} />
            </ul>
            <button type="button" className="house-button quiet" onClick={() => setPanel(null)}>
              Back to the chat
            </button>
          </div>
        )}
        {open && panel === null && (
          <>
            {moneyOpen && other && (
              <MoneySheet
                lifeId={other.lifeId}
                otherName={other.firstName}
                onClose={() => setMoneyOpen(false)}
                onDone={(message, walletPence) => {
                  setMoneyOpen(false);
                  current.put(message);
                  onWallet?.(walletPence);
                }}
              />
            )}
            <MessageList
              shops={
                conversation && conversation.kind !== 'place'
                  ? {
                      mine: myShops,
                      busy: sharing,
                      onShare: (businessId) => void shareShop(conversation.id, businessId),
                      onOpen: onOpenShop,
                    }
                  : null
              }
              visits={
                conversation?.kind === 'direct' && other
                  ? {
                      busy: inviting,
                      onInvite: () => void inviteOver(other.lifeId, other.firstName),
                      onGoOver,
                    }
                  : null
              }
              money={
                conversation?.kind === 'direct' && other
                  ? {
                      otherName: other.firstName,
                      busy: moneyBusy,
                      onOpen: () => setMoneyOpen(true),
                      onAnswer: (requestId, answer) => void answerMoney(requestId, answer),
                    }
                  : null
              }
              messages={current.messages}
              placeholder={
                other
                  ? `Message ${other.firstName}`
                  : conversation?.group
                    ? `Message ${conversation.group.name}`
                    : 'Message'
              }
              sending={current.sending}
              failure={current.failure}
              hasOlder={current.hasOlder}
              onOlder={() => void current.loadOlder()}
              onSend={current.send}
              onEdit={current.edit}
              onDelete={current.remove}
              onReport={current.report}
              onReact={current.react}
              onForward={current.forward}
              conversationId={conversation?.id ?? null}
              myUserId={myUserId ?? null}
              showSeen={conversation?.kind === 'direct'}
              quickReplies={conversation?.kind === 'direct' || conversation?.kind === 'group'}
              voice={
                conversation?.voice
                  ? {
                      canSend: conversation.voice.canSend,
                      blockedText: other
                        ? `${other.firstName} hasn’t allowed voice notes from you yet. Send a message instead.`
                        : 'Voice notes are off here.',
                    }
                  : null
              }
              onVoice={current.sendVoice}
              banner={
                other && conversation ? (
                  <>
                    <p className="chat-private">🔒 Private · only you and {other.firstName} can see this</p>
                    {conversation.request && (
                      <div className="chat-request-note" role="note">
                        <p>
                          {other.firstName} hasn’t chatted with you before. Reply to move this to your chats, or delete
                          it.
                        </p>
                        <div className="chat-panel-actions">
                          <button
                            type="button"
                            className="house-button quiet"
                            onClick={() => void current.clear(true).then((ok) => ok && onOpen(null))}
                          >
                            Delete
                          </button>
                          <button
                            type="button"
                            className="house-button quiet chat-leave"
                            onClick={() => void actOnCard(other.lifeId, { action: 'block' })}
                          >
                            Block {other.firstName}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : null
              }
              emptyText="No messages yet. Say hello."
            />
          </>
        )}
      </div>
    </div>
  );
}
